import type { ReactNode } from 'react';
import { useState } from 'react';
import { AppLayout as CloudscapeAppLayout } from '@cloudscape-design/components';
import { TopNavigation } from './TopNavigation';
import { MobileNavigation } from '../navigation/MobileNavigation';
import { NetworkStatusIndicator } from '../common/NetworkStatusIndicator';
import { useIsMobile } from '../../hooks/useResponsive';
import type { SideNavigationProps as CloudscapeSideNavigationProps } from '@cloudscape-design/components';
import './AppLayout.css';

interface AppLayoutProps {
  children: ReactNode;
  navigation: ReactNode;
  user?: {
    name: string;
    email: string;
  };
  onLogout: () => void;
  navigationOpen?: boolean;
  onNavigationChange?: (open: boolean) => void;
  activeHref?: string;
  onNavigationFollow?: (event: CustomEvent<CloudscapeSideNavigationProps.FollowDetail>) => void;
}

export function AppLayout({
  children,
  navigation,
  user,
  onLogout,
  navigationOpen = true,
  onNavigationChange,
  activeHref = '/',
  onNavigationFollow
}: AppLayoutProps) {
  const isMobile = useIsMobile();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const handleMobileNavToggle = (open: boolean) => {
    setMobileNavOpen(open);
  };

  const handleMobileNavFollow = (event: CustomEvent<CloudscapeSideNavigationProps.FollowDetail>) => {
    if (onNavigationFollow) {
      onNavigationFollow(event);
    }
    setMobileNavOpen(false);
  };

  return (
    <div className="ttec-app-container">
      <TopNavigation 
        user={user} 
        onLogout={onLogout}
        mobileNavToggle={
          isMobile ? (
            <MobileNavigation
              activeHref={activeHref}
              onFollow={handleMobileNavFollow}
              isOpen={mobileNavOpen}
              onToggle={handleMobileNavToggle}
            />
          ) : undefined
        }
      />
      
      {/* Network status banner - shows when offline or backend unreachable */}
      <NetworkStatusIndicator
        variant="banner"
        showRetry={true}
        autoHide={true}
      />
      
      <CloudscapeAppLayout
        navigation={!isMobile ? navigation : null}
        content={children}
        navigationOpen={!isMobile && navigationOpen}
        onNavigationChange={({ detail }) => !isMobile && onNavigationChange?.(detail.open)}
        toolsHide
        ariaLabels={{
          navigation: 'Side navigation',
          navigationClose: 'Close side navigation',
          navigationToggle: 'Open side navigation',
        }}
      />
    </div>
  );
}
